/**
 * seed.ts — 前端种子数据（兜底）
 *
 * 当后端 API（D1）不可用时，首页 / 分类页 / 详情页使用这份数据渲染，
 * 保证「无外部服务也能正常浏览」这一验收标准成立。
 * 后端 Workers 的 src/seed.ts 维护同一份业务数据（部署到 D1）。
 */

export interface Prompt {
  id: string;
  title: string;
  content: string;
  category: string;
  author: string;
  coverUrl?: string;
  createdAt: string;
  likes: number;
}

export interface Category {
  slug: string;
  name: string;
  count: number;
}

/** 分类 slug 与中文名的映射 */
export const categoryNameMap: Record<string, string> = {
  writing: '写作',
  coding: '编程',
  painting: '绘画',
  data: '数据分析',
  office: '办公',
};

/** 取分类中文名，未知 slug 原样返回 */
export function categoryName(slug: string): string {
  return categoryNameMap[slug] ?? slug;
}

export const seedCategories: Category[] = [
  { slug: 'writing', name: '写作', count: 3 },
  { slug: 'coding', name: '编程', count: 3 },
  { slug: 'painting', name: '绘画', count: 2 },
  { slug: 'data', name: '数据分析', count: 2 },
  { slug: 'office', name: '办公', count: 2 },
];

export const seedPrompts: Prompt[] = [
  {
    id: 'p1',
    title: '公众号爆款标题生成器',
    category: 'writing',
    author: '齐活林',
    createdAt: '2026-08-14T10:00:00.000Z',
    likes: 128,
    content:
      '你是一位拥有 10 年经验的公众号主编，擅长把枯燥内容包装成高点击标题。请根据我给出的主题生成 10 个标题候选，要求：\n1. 覆盖悬念型、数字型、痛点型、反差型四种风格；\n2. 每个标题不超过 30 个字；\n3. 拒绝标题党，不夸大、不虚假承诺；\n4. 标注每个标题的预期点击率档位（高 / 中 / 低）。\n我的主题是：「……」',
  },
  {
    id: 'p2',
    title: '小红书种草文案助手',
    category: 'writing',
    author: 'Alice',
    createdAt: '2026-08-13T09:30:00.000Z',
    likes: 96,
    content:
      '你是一位小红书爆款笔记作者，深谙平台调性与用户心理。请根据我提供的产品 / 场景，输出一篇种草笔记：\n1. 先给 5 个备选标题；\n2. 正文 300 字左右，口语化表达，段落短、节奏感强；\n3. 包含 1 个真实使用感受和 1 个避坑提醒；\n4. 结尾给出 3 个引导互动的提问。\n我的产品 / 场景是：「……」',
  },
  {
    id: 'p3',
    title: '商务邮件写作助手',
    category: 'writing',
    author: '寇豆码',
    createdAt: '2026-08-12T14:20:00.000Z',
    likes: 75,
    content:
      '你是专业商务写作顾问，擅长把复杂信息写成简洁得体的邮件。请根据我描述的场景帮我写一封邮件，包含：\n1. 主题行（不超过 20 个字，突出核心信息）；\n2. 称呼与开场；\n3. 正文三段以内，条理清晰；\n4. 结尾与署名建议。\n语气要求：礼貌、清晰、不啰嗦。\n我的场景是：「……」',
  },
  {
    id: 'p4',
    title: '资深代码评审专家',
    category: 'coding',
    author: 'Bob',
    createdAt: '2026-08-14T08:00:00.000Z',
    likes: 210,
    content:
      '你是一位资深软件工程师，正在做严格的代码评审。请审查我给出的代码，输出结构化评审意见：\n1. 总体评价（2-3 句）；\n2. Bug 与隐患，按严重程度从高到低排序，注明问题行号；\n3. 可读性建议；\n4. 性能与安全建议；\n5. 给出改进后的关键代码片段。\n请用中文回答，语气专业且具体。\n我的代码是：「……」',
  },
  {
    id: 'p5',
    title: '正则表达式生成器',
    category: 'coding',
    author: '寇豆码',
    createdAt: '2026-08-11T16:40:00.000Z',
    likes: 88,
    content:
      '你是正则表达式专家。请根据我描述的需求生成正则表达式，并输出：\n1. 可直接使用的表达式；\n2. 用通俗语言逐部分解释；\n3. 3 个匹配示例与 3 个不匹配示例；\n4. 常见陷阱与优化建议（如贪婪匹配、回溯风险）。\n我的需求是：「……」',
  },
  {
    id: 'p6',
    title: 'SQL 查询优化大师',
    category: 'coding',
    author: 'Alice',
    createdAt: '2026-08-10T11:10:00.000Z',
    likes: 143,
    content:
      '你是数据库性能优化专家。请分析我给出的 SQL：\n1. 指出潜在性能问题（全表扫描、索引缺失、N+1 查询、隐式类型转换等）；\n2. 给出优化后的 SQL；\n3. 建议需要创建的索引（含字段顺序理由）；\n4. 说明 EXPLAIN 关键指标怎么看（type、rows、Extra）。\n我的 SQL 是：「……」',
  },
  {
    id: 'p7',
    title: 'Midjourney 摄影级人像提示词',
    category: 'painting',
    author: '齐活林',
    createdAt: '2026-08-13T18:00:00.000Z',
    likes: 176,
    content:
      '请将我的中文描述翻译成一组适用于 Midjourney 的英文提示词，要求：\n1. 覆盖主体、环境、光线、镜头、画质五个维度；\n2. 给出 --ar 构图建议与 --v 版本建议；\n3. 给出 3 个风格变体：写实、电影感、极简；\n4. 最后用中文说明每个关键词的作用。\n我的主题是：「……」',
  },
  {
    id: 'p8',
    title: '国风水墨插画大师',
    category: 'painting',
    author: 'Bob',
    createdAt: '2026-08-09T09:00:00.000Z',
    likes: 132,
    content:
      '你是一位国画创作导师，擅长水墨、留白与意境表达。请根据我的主题输出：\n1. 画面构图建议（远近、疏密、留白比例）；\n2. 用墨与淡彩搭配思路；\n3. 适用于 AI 绘画工具的中英文提示词各一版；\n4. 常见败笔与规避方法（如墨色过脏、构图过满）。\n我的主题是：「……」',
  },
  {
    id: 'p9',
    title: 'Excel 数据分析师',
    category: 'data',
    author: 'Alice',
    createdAt: '2026-08-12T10:30:00.000Z',
    likes: 64,
    content:
      '你是一位精通 Excel 与数据分析的专家。请根据我提供的数据表结构（列名与业务含义）完成：\n1. 数据清洗建议（缺失值、重复值、异常值）；\n2. 3 个可落地的分析维度；\n3. 每个维度对应的 Excel 公式或透视表操作步骤；\n4. 最适合展示的图表类型及原因。\n我的表结构是：「……」',
  },
  {
    id: 'p10',
    title: '数据可视化选型顾问',
    category: 'data',
    author: '寇豆码',
    createdAt: '2026-08-08T15:20:00.000Z',
    likes: 57,
    content:
      '你是数据可视化顾问。请根据我的数据特征（数据量、维度数量、时间粒度、受众）推荐 1-3 种最合适的图表类型，并说明：\n1. 推荐理由（对比 / 构成 / 分布 / 趋势）；\n2. 适用工具（ECharts / Tableau / 自研图表库）；\n3. 实现要点与常见坑（坐标轴截断、颜色误导等）。\n我的数据是：「……」',
  },
  {
    id: 'p11',
    title: '高效会议纪要整理',
    category: 'office',
    author: '齐活林',
    createdAt: '2026-08-11T13:00:00.000Z',
    likes: 82,
    content:
      '你是高级行政助理，擅长把杂乱会议记录整理成结构化纪要。请把下面内容整理为：\n1. 会议主题与时间；\n2. 参会人；\n3. 讨论要点（按议题分组，保留关键分歧）；\n4. 结论与决策；\n5. 待办事项清单（负责人 + 截止时间）。\n请保持客观，不添加原记录没有的信息。\n原始会议记录：「……」',
  },
  {
    id: 'p12',
    title: 'PPT 大纲与演讲稿生成',
    category: 'office',
    author: 'Bob',
    createdAt: '2026-08-07T10:00:00.000Z',
    likes: 71,
    content:
      '你是企业培训师与 PPT 专家。请根据我的主题与时长输出：\n1. 10 页以内的大纲，每页一句话核心观点；\n2. 每页演讲要点（3 条以内）；\n3. 30 秒开场白与 15 秒结束语；\n4. 配色、字体与排版建议。\n我的主题是：「……」；时长：30 分钟。',
  },
];
